import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Search,
  Filter,
  RefreshCw,
  ArrowLeft,
  Loader2,
  Users,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Calendar,
  MapPin,
  Mail,
  Phone,
  Globe,
  Eye,
  UserPlus,
  DollarSign,
  Building2,
  FileText,
  Download,
  Clock,
  Tag,
  Link2,
  Copy,
  ChevronRight,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEAD_TYPES = [
  'medicare', 'health_insurance', 'medicaid', 'dental', 'vision',
  'auto_insurance', 'life_insurance', 'home_insurance', 'commercial_insurance',
  'solar', 'roofing', 'hvac', 'windows', 'home_security',
  'legal', 'education', 'finance', 'debt_relief', 'tax_services', 'business_leads', 'general'
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
];

export default function AdminLeadsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [apiKeys, setApiKeys] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    lead_type: 'all',
    state: 'all',
    source_key: 'all',
    is_duplicate: 'all',
    date_from: '',
    date_to: ''
  });
  
  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  
  // Selection
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Lead details
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  
  // Assignment
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assigningLead, setAssigningLead] = useState(null);
  const [eligibleClients, setEligibleClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [loadingEligible, setLoadingEligible] = useState(false);

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dme_token');
      const params = {
        limit: pageSize,
        skip: page * pageSize
      };
      
      if (filters.search) params.search = filters.search;
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.lead_type !== 'all') params.lead_type = filters.lead_type;
      if (filters.state !== 'all') params.state = filters.state;
      if (filters.source_key !== 'all') params.source_key_id = filters.source_key;
      if (filters.is_duplicate !== 'all') params.is_duplicate = filters.is_duplicate === 'true';
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      const res = await axios.get(`${API_URL}/api/admin/leads/search`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setLeads(res.data.leads || []);
      setTotalCount(res.data.total || 0);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  const fetchApiKeys = useCallback(async () => {
    try {
      const token = localStorage.getItem('dme_token');
      const res = await axios.get(`${API_URL}/api/admin/lead-intake/api-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiKeys(res.data.api_keys || []);
    } catch (error) {
      console.error('Error fetching API keys:', error);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
    fetchApiKeys();
  }, [fetchLeads, fetchApiKeys]);

  const handleSearch = () => {
    setPage(0);
    fetchLeads();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSelectLead = (leadId, checked) => {
    if (checked) {
      setSelectedLeads(prev => [...prev, leadId]);
    } else {
      setSelectedLeads(prev => prev.filter(id => id !== leadId));
    }
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedLeads(leads.map(l => l.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleViewLead = async (lead) => {
    setSelectedLead(lead);
    setShowDetails(true);
  };

  const handleAssignClick = async (lead) => {
    setAssigningLead(lead);
    setSelectedClient(null);
    setShowAssignDialog(true);
    await fetchEligibleClients(lead);
  };

  const fetchEligibleClients = async (lead) => {
    try {
      setLoadingEligible(true);
      const token = localStorage.getItem('dme_token');
      const res = await axios.get(`${API_URL}/api/admin/leads/${lead.id}/eligible-clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEligibleClients(res.data.clients || []);
    } catch (error) {
      console.error('Error fetching eligible clients:', error);
      toast.error('Failed to load eligible clients');
    } finally {
      setLoadingEligible(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedClient || !assigningLead) return;

    try {
      setAssigning(true);
      const token = localStorage.getItem('dme_token');
      await axios.post(
        `${API_URL}/api/admin/leads/${assigningLead.id}/assign`,
        { 
          client_id: selectedClient.client_id,
          campaign_id: selectedClient.campaign_id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Lead assigned to ${selectedClient.client_name}`);
      setShowAssignDialog(false);
      setAssigningLead(null);
      setSelectedClient(null);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign lead');
    } finally {
      setAssigning(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('dme_token');
      const leadIds = selectedLeads.length > 0 ? selectedLeads : leads.map(l => l.id);
      
      const res = await axios.post(
        `${API_URL}/api/admin/leads/export`,
        { lead_ids: leadIds },
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Exported ${leadIds.length} leads`);
    } catch (error) {
      toast.error('Failed to export leads');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'assigned':
        return <Badge className="bg-green-100 text-green-800">Assigned</Badge>;
      case 'unassigned':
        return <Badge variant="secondary">Unassigned</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6 p-6" data-testid="admin-leads-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Users className="w-6 h-6 text-teal-600" />
              Lead Management
            </h1>
            <p className="text-muted-foreground">Search, view, and assign leads to clients</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selectedLeads.length > 0 && (
            <Badge variant="outline" className="mr-2">
              {selectedLeads.length} selected
            </Badge>
          )}
          <Button onClick={handleExport} variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button onClick={fetchLeads} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {/* Search */}
            <div className="col-span-2">
              <Label className="text-xs text-muted-foreground">Search</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Name, email, phone, confirmation ID..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch} size="icon">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Status */}
            <div>
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={filters.status} onValueChange={(v) => handleFilterChange('status', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Lead Type */}
            <div>
              <Label className="text-xs text-muted-foreground">Lead Type</Label>
              <Select value={filters.lead_type} onValueChange={(v) => handleFilterChange('lead_type', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {LEAD_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* State */}
            <div>
              <Label className="text-xs text-muted-foreground">State</Label>
              <Select value={filters.state} onValueChange={(v) => handleFilterChange('state', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  {US_STATES.map(state => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Source */}
            <div>
              <Label className="text-xs text-muted-foreground">Source</Label>
              <Select value={filters.source_key} onValueChange={(v) => handleFilterChange('source_key', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {apiKeys.map(key => (
                    <SelectItem key={key.id} value={key.id}>{key.source_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Duplicate Filter */}
            <div>
              <Label className="text-xs text-muted-foreground">Duplicate</Label>
              <Select value={filters.is_duplicate} onValueChange={(v) => handleFilterChange('is_duplicate', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Leads</SelectItem>
                  <SelectItem value="false">Original Only</SelectItem>
                  <SelectItem value="true">Duplicates Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date From */}
            <div>
              <Label className="text-xs text-muted-foreground">Date From</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
              />
            </div>

            {/* Date To */}
            <div>
              <Label className="text-xs text-muted-foreground">Date To</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
              />
            </div>

            {/* Filter Button */}
            <div className="flex items-end">
              <Button onClick={handleSearch} className="w-full">
                <Filter className="w-4 h-4 mr-2" />
                Apply Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>Showing {leads.length} of {totalCount} leads</span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            Previous
          </Button>
          <span>Page {page + 1}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => p + 1)}
            disabled={leads.length < pageSize}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Leads Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No leads found matching your criteria</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectAll}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Confirmation ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Source</TableHead>
                <TableHead>Lead Score</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow 
                    key={lead.id} 
                    className={`${lead.is_duplicate ? 'bg-orange-50' : ''} hover:bg-gray-50 cursor-pointer`}
                    onClick={() => handleViewLead(lead)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedLeads.includes(lead.id)}
                        onCheckedChange={(checked) => handleSelectLead(lead.id, checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="text-xs font-mono">{lead.confirmation_id}</code>
                        {lead.is_duplicate && (
                          <Badge variant="outline" className="text-orange-600 text-xs">DUP</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      {lead.first_name} {lead.last_name}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm space-y-1">
                        <div className="flex items-center gap-1">
                          <Mail className="w-3 h-3 text-muted-foreground" />
                          <span className="truncate max-w-[150px]">{lead.email || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Phone className="w-3 h-3" />
                          {lead.phone || 'N/A'}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-muted-foreground" />
                        {lead.city ? `${lead.city}, ` : ''}{lead.state || 'N/A'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize text-xs">
                        {lead.lead_type?.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs">{lead.source_name}</code>
                    </TableCell>
                    <TableCell data-testid={`admin-lead-score-${lead.id}`}>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{lead.lead_score ?? '-'}</Badge>
                        {lead.verification_failed && <Badge variant="destructive" data-testid={`admin-lead-verification-failed-${lead.id}`}>Verify</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(lead.routing_status)}
                      {lead.assigned_to?.length > 0 && (
                        <span className="text-xs text-muted-foreground ml-1">
                          ({lead.assigned_to.length})
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(lead.submitted_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleViewLead(lead)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {lead.routing_status !== 'assigned' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-teal-600"
                            onClick={() => handleAssignClick(lead)}
                          >
                            <UserPlus className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Lead Details Sheet */}
      <Sheet open={showDetails} onOpenChange={setShowDetails}>
        <SheetContent className="w-[500px] sm:max-w-[500px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Lead Details
            </SheetTitle>
            <SheetDescription>
              {selectedLead?.confirmation_id}
            </SheetDescription>
          </SheetHeader>
          
          {selectedLead && (
            <ScrollArea className="h-[calc(100vh-120px)] mt-4">
              <div className="space-y-6 pr-4">
                {/* Status */}
                <div className="flex items-center justify-between">
                  {getStatusBadge(selectedLead.routing_status)}
                  {selectedLead.is_duplicate && (
                    <Badge variant="outline" className="text-orange-600">Duplicate</Badge>
                  )}
                </div>

                {/* Contact Info */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Contact Information
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <Label className="text-xs text-muted-foreground">First Name</Label>
                      <p className="font-medium">{selectedLead.first_name}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Last Name</Label>
                      <p className="font-medium">{selectedLead.last_name}</p>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs text-muted-foreground">Email</Label>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{selectedLead.email || 'N/A'}</p>
                        {selectedLead.email && (
                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copyToClipboard(selectedLead.email)}>
                            <Copy className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs text-muted-foreground">Phone</Label>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{selectedLead.phone || 'N/A'}</p>
                        {selectedLead.phone && (
                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copyToClipboard(selectedLead.phone)}>
                            <Copy className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Location */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Location
                  </h4>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <Label className="text-xs text-muted-foreground">State</Label>
                      <p className="font-medium">{selectedLead.state || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">City</Label>
                      <p className="font-medium">{selectedLead.city || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Zip</Label>
                      <p className="font-medium">{selectedLead.zip_code || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Lead Details */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    Lead Details
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <Label className="text-xs text-muted-foreground">Lead Type</Label>
                      <Badge variant="outline" className="capitalize mt-1">
                        {selectedLead.lead_type?.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Source</Label>
                      <p className="font-medium">{selectedLead.source_name}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Lead Score</Label>
                      <p className="font-medium" data-testid="admin-lead-details-score">{selectedLead.lead_score ?? '-'}</p>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs text-muted-foreground">Landing Page</Label>
                      {selectedLead.landing_page_url ? (
                        <a 
                          href={selectedLead.landing_page_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline flex items-center gap-1 text-sm"
                        >
                          <Link2 className="w-3 h-3" />
                          {selectedLead.landing_page_url}
                        </a>
                      ) : (
                        <p className="text-muted-foreground">N/A</p>
                      )}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Timestamps */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Timestamps
                  </h4>
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Submitted</span>
                      <span>{formatDate(selectedLead.submitted_at)}</span>
                    </div>
                    {selectedLead.ip_address && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">IP Address</span>
                        <span>{selectedLead.ip_address}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Assignments */}
                {selectedLead.assigned_to?.length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-3">
                      <h4 className="font-semibold text-sm flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        Assigned To ({selectedLead.assigned_to.length})
                      </h4>
                      <div className="space-y-2">
                        {selectedLead.assigned_to.map((clientId, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-gray-50 rounded">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span>{clientId}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Assign Button */}
                {selectedLead.routing_status !== 'assigned' && (
                  <Button 
                    className="w-full mt-4" 
                    onClick={() => {
                      setShowDetails(false);
                      handleAssignClick(selectedLead);
                    }}
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Assign to Client
                  </Button>
                )}
              </div>
            </ScrollArea>
          )}
        </SheetContent>
      </Sheet>

      {/* Assignment Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Assign Lead to Client
            </DialogTitle>
            <DialogDescription>
              {assigningLead?.confirmation_id} - {assigningLead?.first_name} {assigningLead?.last_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Lead Summary */}
            <div className="p-3 bg-gray-50 rounded-lg text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-muted-foreground">State:</span>{' '}
                  <span className="font-medium">{assigningLead?.state || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Type:</span>{' '}
                  <Badge variant="outline" className="capitalize text-xs">
                    {assigningLead?.lead_type?.replace(/_/g, ' ')}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Eligible Clients */}
            <div>
              <Label className="mb-2 block">Select Client</Label>
              {loadingEligible ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : eligibleClients.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-orange-500" />
                  <p>No eligible clients found</p>
                  <p className="text-xs">No active campaigns match this lead's state and type</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {eligibleClients.map((client) => (
                    <div
                      key={`${client.client_id}-${client.campaign_id}`}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedClient?.client_id === client.client_id && 
                        selectedClient?.campaign_id === client.campaign_id
                          ? 'border-teal-500 bg-teal-50'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedClient(client)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{client.client_name}</p>
                          <p className="text-sm text-muted-foreground">{client.campaign_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-green-600">{formatCurrency(client.bid)}</p>
                          <p className="text-xs text-muted-foreground">
                            Balance: {formatCurrency(client.balance)}
                          </p>
                        </div>
                      </div>
                      {client.balance < client.bid && (
                        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Insufficient balance
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Selected Summary */}
            {selectedClient && (
              <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{selectedClient.client_name}</p>
                    <p className="text-sm text-muted-foreground">{selectedClient.campaign_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Cost</p>
                    <p className="font-bold text-lg text-teal-700">{formatCurrency(selectedClient.bid)}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssign} 
              disabled={!selectedClient || assigning || (selectedClient && selectedClient.balance < selectedClient.bid)}
            >
              {assigning ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Assign Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
